import type { Vec2 } from '@vellum/core';

/** Ramer–Douglas–Peucker simplification. */
function simplify(pts: Vec2[], eps: number): Vec2[] {
  if (pts.length < 3) return pts;
  const keep = new Uint8Array(pts.length);
  keep[0] = keep[pts.length - 1] = 1;
  const stack: [number, number][] = [[0, pts.length - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    let maxD = 0,
      idx = -1;
    const A = pts[a],
      B = pts[b];
    const dx = B.x - A.x,
      dy = B.y - A.y;
    const len = Math.hypot(dx, dy) || 1;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs(dy * pts[i].x - dx * pts[i].y + B.x * A.y - B.y * A.x) / len;
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (maxD > eps && idx > 0) {
      keep[idx] = 1;
      stack.push([a, idx], [idx, b]);
    }
  }
  return pts.filter((_, i) => keep[i]);
}

function bounds(pts: Vec2[]) {
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const p of pts) {
    x0 = Math.min(x0, p.x);
    y0 = Math.min(y0, p.y);
    x1 = Math.max(x1, p.x);
    y1 = Math.max(y1, p.y);
  }
  return { x0, y0, x1, y1 };
}

const f = (n: number) => +n.toFixed(2);

/** Freehand stroke → smooth cubic path in node-local coordinates. */
export function pencilToPath(raw: Vec2[], width: number): { x: number; y: number; width: number; height: number; path: string } | null {
  const pts = simplify(raw, Math.max(0.5, width * 0.15));
  if (pts.length < 2) return null;
  const b = bounds(pts);
  const ox = b.x0,
    oy = b.y0;
  const L = pts.map((p) => ({ x: p.x - ox, y: p.y - oy }));
  let d = `M${f(L[0].x)} ${f(L[0].y)}`;
  if (L.length === 2) d += `L${f(L[1].x)} ${f(L[1].y)}`;
  else {
    // Catmull-Rom → Bézier
    for (let i = 0; i < L.length - 1; i++) {
      const p0 = L[i - 1] ?? L[i];
      const p1 = L[i];
      const p2 = L[i + 1];
      const p3 = L[i + 2] ?? p2;
      const c1 = { x: p1.x + (p2.x - p0.x) / 6, y: p1.y + (p2.y - p0.y) / 6 };
      const c2 = { x: p2.x - (p3.x - p1.x) / 6, y: p2.y - (p3.y - p1.y) / 6 };
      d += `C${f(c1.x)} ${f(c1.y)} ${f(c2.x)} ${f(c2.y)} ${f(p2.x)} ${f(p2.y)}`;
    }
  }
  return { x: ox, y: oy, width: Math.max(0.01, b.x1 - b.x0), height: Math.max(0.01, b.y1 - b.y0), path: d };
}

/** Pen tool points → straight-segment path. */
export function penToPath(pts: Vec2[], closed: boolean): { x: number; y: number; width: number; height: number; path: string } {
  const b = bounds(pts);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${f(p.x - b.x0)} ${f(p.y - b.y0)}`).join('') + (closed ? 'Z' : '');
  return { x: b.x0, y: b.y0, width: Math.max(0.01, b.x1 - b.x0), height: Math.max(0.01, b.y1 - b.y0), path: d };
}
